import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import {
  type NormalizedEntityWriteMeta,
  type NormalizedEntityWriteResult,
  writeNormalizedEntitySnapshot,
} from './normalized-entity-write.js';
import {
  materializePublicUserIdentity,
  publicUserEntityKey,
  upsertPublicUserEntity,
} from './public-user-entity.js';

const VIEWER_FIELDS = ['viewerIsContributor', 'canClaim', 'canWithdraw'] as const;

export interface GiftEntityScope {
  readonly network: string;
  readonly viewerScope: string;
}

export interface GiftEntityDiagnostic {
  readonly kind:
    | 'alias-mismatch'
    | 'stale-event-rejected'
    | 'missing-entity-recovery'
    | 'visibility-boundary';
  readonly entityHash: string;
  readonly aliasHash?: string;
}

export interface GiftEntityWriteResult extends NormalizedEntityWriteResult {
  readonly giftPda: string | null;
}

export interface GiftCollectionIndex {
  readonly ids: readonly string[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

export function giftEntityKey(network: string, giftPda: string): QueryKey {
  return ['gifts', 'entity', network, giftPda];
}

export function giftAliasKey(network: string, opaqueId: string): QueryKey {
  return ['gifts', 'alias', network, opaqueId];
}

export function giftViewerOverlayKey(scope: GiftEntityScope, giftPda: string): QueryKey {
  return ['gifts', 'viewer', scope.network, scope.viewerScope, giftPda];
}

export function giftCollectionKey(scope: GiftEntityScope, collection: string): QueryKey {
  return ['gifts', 'index', scope.network, scope.viewerScope, collection];
}

export function resolveGiftEntityId(
  queryClient: QueryClient,
  network: string,
  opaqueId: string,
): string | undefined {
  if (opaqueId.length === 0) return undefined;
  const alias = queryClient.getQueryData<unknown>(giftAliasKey(network, opaqueId));
  if (typeof alias === 'string') return alias;
  if (alias === null || typeof alias !== 'object') return undefined;
  return nonEmptyString((alias as Record<string, unknown>).giftPda);
}

/**
 * Writes public gift state once by network + gift PDA. The permalink opaque id
 * is an alias only; viewer affordances are written to a separate scoped row.
 */
export function upsertGiftEntity(
  queryClient: QueryClient,
  input: Record<string, unknown>,
  scope: GiftEntityScope,
  meta: NormalizedEntityWriteMeta,
): GiftEntityWriteResult {
  const giftPda = nonEmptyString(input.giftPda);
  if (giftPda === undefined) {
    recordGiftEntityDiagnostic({
      kind: 'alias-mismatch',
      entityHash: hashDiagnosticEntity('missing-gift-pda'),
    });
    return { status: 'stale', giftPda: null };
  }
  const opaqueId = nonEmptyString(input.opaqueId);
  if (opaqueId !== undefined) {
    const existing = resolveGiftEntityId(queryClient, scope.network, opaqueId);
    if (existing !== undefined && existing !== giftPda) {
      recordGiftEntityDiagnostic({
        kind: 'alias-mismatch',
        entityHash: hashDiagnosticEntity(giftPda),
        aliasHash: hashDiagnosticEntity(opaqueId),
      });
      return { status: 'stale', giftPda };
    }
    writeNormalizedEntitySnapshot(
      queryClient,
      giftAliasKey(scope.network, opaqueId),
      { giftPda },
      meta,
    );
  }

  const { canonical, overlay } = splitGiftViewerOverlay(input);
  const canonicalWithPublicUsers = normalizeGiftPublicUsers(queryClient, canonical, meta);
  const result = writeNormalizedEntitySnapshot(
    queryClient,
    giftEntityKey(scope.network, giftPda),
    canonicalWithPublicUsers,
    meta,
  );
  if (overlay !== undefined) {
    if (scope.viewerScope.length === 0) {
      recordGiftEntityDiagnostic({
        kind: 'visibility-boundary',
        entityHash: hashDiagnosticEntity(giftPda),
      });
    } else {
      writeNormalizedEntitySnapshot(
        queryClient,
        giftViewerOverlayKey(scope, giftPda),
        overlay,
        meta,
      );
    }
  }
  return { ...result, giftPda };
}

/** Materialize every gift row in a route response at the source boundary. */
export function writeGiftEntityResponse(
  queryClient: QueryClient,
  response: unknown,
  scope: GiftEntityScope,
  meta: NormalizedEntityWriteMeta,
): void {
  if (response === null || typeof response !== 'object') return;
  const record = response as Record<string, unknown>;
  const rows = Array.isArray(record.data) ? record.data : [record];
  for (const row of rows) {
    if (row !== null && typeof row === 'object') {
      upsertGiftEntity(queryClient, row as Record<string, unknown>, scope, meta);
    }
  }
}

/** Wrap a generated reducer so its route cache and canonical gift rows move
 * in the same synchronous realtime transaction. */
export function makeGiftEntityApplyEvent<T>(
  applyEvent: (previous: T, event: unknown) => T,
  queryClient: QueryClient,
  scope: GiftEntityScope,
): (previous: T, event: unknown) => T {
  return (previous, event) => {
    const next = applyEvent(previous, event);
    writeGiftEntityResponse(queryClient, next, scope, {
      source: 'nats',
      observedAtMs: Date.now(),
    });
    return next;
  };
}

export function readGiftEntity(
  queryClient: QueryClient,
  scope: GiftEntityScope,
  giftPda: string,
): Record<string, unknown> | undefined {
  const canonical = queryClient.getQueryData<Record<string, unknown>>(
    giftEntityKey(scope.network, giftPda),
  );
  if (canonical === undefined) {
    recordGiftEntityDiagnostic({
      kind: 'missing-entity-recovery',
      entityHash: hashDiagnosticEntity(giftPda),
    });
    return undefined;
  }
  const overlay = queryClient.getQueryData<Record<string, unknown>>(
    giftViewerOverlayKey(scope, giftPda),
  );
  return materializeGiftPublicUsers(queryClient, { ...canonical, ...overlay });
}

/** Keeps collections as ordered id indexes; page metadata remains collection-owned. */
export function appendGiftCollectionPage(
  previous: GiftCollectionIndex | undefined,
  page: GiftCollectionIndex,
): GiftCollectionIndex {
  const ids = [...(previous?.ids ?? [])];
  const seen = new Set(ids);
  for (const id of page.ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return { ids, nextCursor: page.nextCursor, hasMore: page.hasMore };
}

/** Sequence is a synthetic, monotonic test/runtime cursor for gift events. */
export function applyGiftEvent(
  queryClient: QueryClient,
  scope: GiftEntityScope,
  event: {
    readonly giftPda: string;
    readonly opaqueId?: string;
    readonly kind: 'created' | 'claimed';
    readonly sequence: number;
    readonly observedAtMs: number;
  },
): GiftEntityWriteResult {
  const key = giftEntityKey(scope.network, event.giftPda);
  const current = queryClient.getQueryData<Record<string, unknown>>(key);
  const priorSequence = giftEventSequences
    .get(queryClient)
    ?.get(eventKey(scope.network, event.giftPda));
  if (priorSequence !== undefined && event.sequence <= priorSequence) {
    recordGiftEntityDiagnostic({
      kind: 'stale-event-rejected',
      entityHash: hashDiagnosticEntity(event.giftPda),
    });
    return { status: 'stale', giftPda: event.giftPda };
  }
  const sequences = giftEventSequences.get(queryClient) ?? new Map<string, number>();
  sequences.set(eventKey(scope.network, event.giftPda), event.sequence);
  giftEventSequences.set(queryClient, sequences);
  return upsertGiftEntity(
    queryClient,
    {
      ...(current ?? { giftPda: event.giftPda }),
      ...(event.opaqueId === undefined ? {} : { opaqueId: event.opaqueId }),
      status: event.kind === 'claimed' ? 'claimed' : (current?.status ?? 'pending'),
      ...(event.kind === 'claimed'
        ? { claimedAt: new Date(event.observedAtMs).toISOString() }
        : {}),
    },
    scope,
    { source: 'nats', observedAtMs: event.observedAtMs },
  );
}

export function resetGiftEntityEventSequences(queryClient: QueryClient): void {
  giftEventSequences.delete(queryClient);
}

function splitGiftViewerOverlay(input: Record<string, unknown>): {
  readonly canonical: Record<string, unknown>;
  readonly overlay: Record<string, unknown> | undefined;
} {
  const canonical = { ...input };
  const overlay: Record<string, unknown> = {};
  for (const field of VIEWER_FIELDS) {
    if (canonical[field] !== undefined) overlay[field] = canonical[field];
    delete canonical[field];
  }
  return { canonical, overlay: Object.keys(overlay).length === 0 ? undefined : overlay };
}

function normalizeGiftPublicUsers(
  queryClient: QueryClient,
  gift: Record<string, unknown>,
  meta: NormalizedEntityWriteMeta,
): Record<string, unknown> {
  const next = { ...gift };
  for (const field of ['sender', 'recipient'] as const) {
    const value = gift[field];
    if (value === null || typeof value !== 'object') continue;
    const result = upsertPublicUserEntity(queryClient, value as Record<string, unknown>, meta);
    if (result.entityId !== null) next[field] = { userId: result.entityId };
  }
  return next;
}

function materializeGiftPublicUsers(
  queryClient: QueryClient,
  gift: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...gift };
  for (const field of ['sender', 'recipient'] as const) {
    const value = gift[field];
    if (value === null || typeof value !== 'object') continue;
    const userId = nonEmptyString((value as Record<string, unknown>).userId);
    if (userId === undefined) continue;
    const canonical = queryClient.getQueryData<Record<string, unknown>>(
      publicUserEntityKey(userId),
    );
    next[field] = materializePublicUserIdentity(value as Record<string, unknown>, canonical);
  }
  return next;
}

function recordGiftEntityDiagnostic(diagnostic: GiftEntityDiagnostic): void {
  emit('gift-entity:diagnostic', diagnostic);
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function eventKey(network: string, giftPda: string): string {
  return `${network}\u0000${giftPda}`;
}

const giftEventSequences = new WeakMap<QueryClient, Map<string, number>>();
